import React, { useState } from 'react'
import {
  formatHoursValue,
  parseHoursValue,
  ROUND_TRIP_HOURS,
  suggestedFullDayPrice,
  suggestedOvernightFee,
} from './CarWithDriverPricing'
import { BottomNav, ProgressHeader, RequiredMark, TopBar } from './CarWithDriverShared'
import { useCwdLang } from './useCwdLang'

export interface RoundTripEntry {
  id: string
  carType: string
  maxHoursPerDay: string
  fullDayPrice: string
  overnightFee: string
  // Legacy drafts only; not collected any more (no column to publish it to).
  overtimeFee?: string
}

const DEFAULT_HOURS = 10

const newEntry = (carType: string, hours: number): RoundTripEntry => ({
  id: `rt-${Date.now()}-${Math.random().toString(36).slice(2)}`,
  carType,
  maxHoursPerDay: formatHoursValue(hours),
  fullDayPrice: String(suggestedFullDayPrice(carType, hours)),
  overnightFee: String(suggestedOvernightFee(carType)),
})

// One entry per car type (the API publishes a single service hour + price per
// car type). Keeps the saved entry when there is one, otherwise starts from the
// suggested prices.
const syncWithCarTypes = (entries: RoundTripEntry[], carTypes: string[]) =>
  carTypes.map((type) => entries.find((e) => e.carType === type) ?? newEntry(type, DEFAULT_HOURS))

const formatPrice = (value: number) =>
  value.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })

interface Props {
  entries: RoundTripEntry[]
  availableCarTypes: string[]
  currentStep: number
  totalSteps: number
  onExit: () => void
  onBack: () => void
  onNext: (entries: RoundTripEntry[]) => void
  onBeforeExit?: (entries: RoundTripEntry[]) => void
  onManageCarTypes: () => void
  submitting?: boolean
}

const CarWithDriverStep3RoundTrip: React.FC<Props> = ({
  entries,
  availableCarTypes,
  currentStep,
  totalSteps,
  onExit,
  onBack,
  onNext,
  onBeforeExit,
  onManageCarTypes,
  submitting = false,
}) => {
  const { t } = useCwdLang()
  const [localEntries, setLocalEntries] = useState<RoundTripEntry[]>(() =>
    syncWithCarTypes(entries, availableCarTypes),
  )
  const [error, setError] = useState('')

  const updateEntry = (id: string, patch: Partial<RoundTripEntry>) => {
    setLocalEntries((prev) => prev.map((e) => (e.id === id ? { ...e, ...patch } : e)))
    setError('')
  }

  const handleHoursChange = (entry: RoundTripEntry, hours: number) => {
    const prevSuggested = String(
      suggestedFullDayPrice(entry.carType, parseHoursValue(entry.maxHoursPerDay)),
    )
    // Follow the suggested price for the new hours unless the owner edited it.
    const untouched = !entry.fullDayPrice || entry.fullDayPrice === prevSuggested
    updateEntry(entry.id, {
      maxHoursPerDay: formatHoursValue(hours),
      ...(untouched && { fullDayPrice: String(suggestedFullDayPrice(entry.carType, hours)) }),
    })
  }

  const handleNext = () => {
    const invalid = localEntries.find((e) => !e.fullDayPrice)
    if (invalid) {
      setError(t.roundTrip.errPrice(invalid.carType))
      return
    }
    setError('')
    onNext(localEntries)
  }

  const handleExit = () => {
    onBeforeExit?.(localEntries)
    onExit()
  }

  const handleManageCarTypes = () => {
    onBeforeExit?.(localEntries)
    onManageCarTypes()
  }

  const priceRow = (
    label: string,
    value: string,
    suffix: string,
    suggested: number,
    onChange: (v: string) => void,
    required = false,
  ) => (
    <div style={{ marginBottom: '16px' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
        <span style={priceLabelStyle}>
          {label}
          {required && <RequiredMark />}
        </span>
        <div style={priceInputWrapperStyle}>
          <input
            type="number"
            inputMode="decimal"
            placeholder="0.00"
            value={value}
            onChange={(e) => onChange(e.target.value)}
            onWheel={(e) => e.currentTarget.blur()}
            style={priceInputStyle}
          />
          <span style={priceSuffixStyle}>{suffix}</span>
        </div>
      </div>
      <p style={suggestedStyle}>{t.roundTrip.suggestedPrice(formatPrice(suggested))}</p>
    </div>
  )

  return (
    <div style={pageStyle}>
      <TopBar onExit={handleExit} />
      <ProgressHeader
        currentStep={currentStep}
        totalSteps={totalSteps}
        title={t.roundTrip.title}
        currentStepProgress={localEntries.length > 0 ? 100 : 0}
      />
      <div style={{ flex: 1, overflowY: 'auto', padding: '0 16px' }}>
        <p style={{ fontSize: '13px', color: '#667085', margin: '0 0 16px', lineHeight: '1.5' }}>
          {t.roundTrip.desc}
        </p>

        <div style={netRevenueBoxStyle}>
          <p style={{ fontSize: '15px', fontWeight: '600', color: '#212427', margin: '0 0 4px' }}>
            {t.common.netRevenueTitle}
          </p>
          <p style={{ fontSize: '13px', color: '#667085', margin: 0, lineHeight: '1.5' }}>
            {t.common.netRevenueDesc}
          </p>
        </div>

        {availableCarTypes.length === 0 && (
          <p style={{ fontSize: '14px', color: '#667085', textAlign: 'center', margin: '24px 0' }}>
            {t.roundTrip.noCarTypes}
          </p>
        )}

        {localEntries.map((entry) => {
          const hours = parseHoursValue(entry.maxHoursPerDay)
          return (
            <div key={entry.id}>
              <p style={fieldLabelStyle}>{t.common.carType}</p>
              <div style={readOnlyBoxStyle}>{entry.carType}</div>

              <p style={fieldLabelStyle}>{t.roundTrip.serviceHourPerDay}</p>
              <div style={hoursRowStyle}>
                {ROUND_TRIP_HOURS.map((h) => (
                  <button
                    key={h}
                    onClick={() => handleHoursChange(entry, h)}
                    style={hourPillStyle(h === hours)}
                  >
                    {t.common.hours(h)}
                  </button>
                ))}
              </div>

              {priceRow(
                t.roundTrip.fullDayPrice,
                entry.fullDayPrice,
                'THB',
                suggestedFullDayPrice(entry.carType, hours),
                (v) => updateEntry(entry.id, { fullDayPrice: v }),
                true,
              )}
              {priceRow(
                t.roundTrip.overnightFee,
                entry.overnightFee,
                t.roundTrip.perNight,
                suggestedOvernightFee(entry.carType),
                (v) => updateEntry(entry.id, { overnightFee: v }),
              )}
              <div style={{ ...dividerStyle, marginBottom: '24px' }} />
            </div>
          )
        })}

        {error && <p style={{ color: '#ef4444', fontSize: '13px', margin: '0 0 12px' }}>{error}</p>}

        <button onClick={handleManageCarTypes} style={manageBtnStyle}>
          {t.roundTrip.manageCarTypes}
        </button>
      </div>
      <BottomNav
        onBack={onBack}
        onNext={handleNext}
        nextDisabled={localEntries.length === 0}
        loading={submitting}
      />
    </div>
  )
}

// ── Styles ───────────────────────────────────────────────────────────────
const pageStyle: React.CSSProperties = {
  fontFamily: 'Poppins, sans-serif',
  backgroundColor: '#fff',
  minHeight: '100vh',
  maxWidth: '500px',
  margin: '0 auto',
  display: 'flex',
  flexDirection: 'column',
  boxSizing: 'border-box',
}

const netRevenueBoxStyle: React.CSSProperties = {
  backgroundColor: '#F4F5F9',
  borderRadius: '8px',
  padding: '16px',
  marginBottom: '24px',
}

const fieldLabelStyle: React.CSSProperties = {
  fontSize: '15px',
  fontWeight: '400',
  color: '#212427',
  margin: '0 0 12px',
}

const readOnlyBoxStyle: React.CSSProperties = {
  padding: '14px 16px',
  border: '1px solid #D6DAE0',
  borderRadius: '8px',
  backgroundColor: '#F4F5F9',
  color: '#667085',
  fontSize: '15px',
  marginBottom: '24px',
}

const hoursRowStyle: React.CSSProperties = {
  display: 'grid',
  gridTemplateColumns: 'repeat(4, 1fr)',
  gap: '8px',
  marginBottom: '20px',
}

const hourPillStyle = (selected: boolean): React.CSSProperties => ({
  padding: '14px 4px',
  borderRadius: '8px',
  border: `1px solid ${selected ? '#0048B3' : '#D6DAE0'}`,
  backgroundColor: selected ? '#EFF6FF' : '#fff',
  color: selected ? '#0048B3' : '#212427',
  fontSize: '14px',
  fontFamily: 'Poppins, sans-serif',
  cursor: 'pointer',
  whiteSpace: 'nowrap',
})

const priceLabelStyle: React.CSSProperties = {
  flex: '0 0 38%',
  fontSize: '15px',
  color: '#667085',
}

const priceInputWrapperStyle: React.CSSProperties = {
  flex: 1,
  display: 'flex',
  alignItems: 'center',
  border: '1px solid #D6DAE0',
  borderRadius: '8px',
  padding: '0 14px',
  minWidth: 0,
}

const priceInputStyle: React.CSSProperties = {
  flex: 1,
  padding: '13px 0',
  border: 'none',
  fontSize: '15px',
  fontFamily: 'Poppins, sans-serif',
  outline: 'none',
  minWidth: 0,
  backgroundColor: 'transparent',
}

const priceSuffixStyle: React.CSSProperties = {
  fontSize: '14px',
  color: '#212427',
  flexShrink: 0,
  whiteSpace: 'nowrap',
  marginLeft: '8px',
}

const suggestedStyle: React.CSSProperties = {
  fontSize: '13px',
  fontStyle: 'italic',
  color: '#667085',
  textAlign: 'right',
  margin: '6px 0 0',
}

const dividerStyle: React.CSSProperties = {
  height: '1px',
  backgroundColor: '#F0F0F0',
  margin: '8px 0 16px',
}

const manageBtnStyle: React.CSSProperties = {
  width: '100%',
  padding: '16px',
  backgroundColor: '#E8F1FF',
  color: '#0048B3',
  border: 'none',
  borderRadius: '28px',
  cursor: 'pointer',
  fontWeight: '500',
  fontSize: '15px',
  fontFamily: 'Poppins, sans-serif',
  marginBottom: '24px',
}

export default CarWithDriverStep3RoundTrip
